// DyslexiaScreenerForm.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col } from "react-bootstrap";
import { getTask, saveTaskAnswers } from "../services/APIClient";

// Import teacher templates
import TeacherRealWordReadingA from "../components/TeacherRealWordReadingA";

function DyslexiaScreenerForm() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    studentId,
    studentName,
    assessmentInstanceId,
    assessmentId,
    task: initialTask,
    taskIndex: initialTaskIndex,
    numTasks: initialNumTasks,
    assessorName,
    assessorEmail,
  } = location.state || {};

  console.log("[DyslexiaScreenerForm] location.state:", location.state);

  const [task, setTask] = useState(initialTask);
  const [taskIndex, setTaskIndex] = useState(initialTaskIndex);
  const [numTasks] = useState(initialNumTasks);
  const [teacherResponses, setTeacherResponses] = useState({});
  const [taskComponent, setTaskComponent] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isLastTask = taskIndex >= numTasks;
  const progressPercent = numTasks ? Math.round((taskIndex / numTasks) * 100) : 0;

  // Function to map task ID to teacher component
  function getTeacherTaskComponent(taskData, studentId, assessmentInstanceId, taskIndex, numTasks, taskId) {
    switch (taskId) {
      case "66a117a7-7f37-490b-84ed-6320c1673902":
        return (
          <TeacherRealWordReadingA
            taskData={taskData}
            studentId={studentId}
            assessmentInstanceId={assessmentInstanceId}
            taskIndex={taskIndex}
            numTasks={numTasks}
            taskId={taskId}
            teacherResponses={teacherResponses}
            setTeacherResponses={setTeacherResponses}
          />
        );
      
      // Add more cases here as you build more teacher templates
      
      default:
        return (
          <div style={{ textAlign: "center", color: "#999", padding: "48px" }}>
            <p>No teacher template found for task ID: {taskId}</p>
          </div>
        );
    }
  }

  // Load task component when task changes
  useEffect(() => {
    console.log("[DyslexiaScreenerForm] task changed:", task);
    console.log("[DyslexiaScreenerForm] teacherResponses:", teacherResponses);
    if (task?.taskid) {
      const component = getTeacherTaskComponent(
        task,
        studentId,
        assessmentInstanceId,
        taskIndex,
        numTasks,
        task.taskid
      );
      setTaskComponent(component);
    }
  }, [task, studentId, assessmentInstanceId, taskIndex, numTasks, teacherResponses]);

  const handleNext = async () => {
    console.log("[DyslexiaScreenerForm] handleNext called — taskIndex:", taskIndex, "isLastTask:", isLastTask);
    console.log("[DyslexiaScreenerForm] saving teacherResponses:", teacherResponses);
    setSubmitting(true);
    setError(null);

    try {
      // Save teacher's captured responses instead of empty object
      await saveTaskAnswers(studentId, assessmentInstanceId, task?.taskid, teacherResponses);

      if (isLastTask) {
        navigate("/dyslexiascreenercomplete", {
          state: { studentId, studentName, assessmentInstanceId, assessmentId },
        });
        return;
      }

      const nextTask = await getTask(studentId, assessmentInstanceId);
      console.log("[DyslexiaScreenerForm] nextTask received:", nextTask);
      setTask(nextTask);
      setTaskIndex(nextTask?.index ?? taskIndex + 1);
      setTeacherResponses({}); // Reset for next task
    } catch (err) {
      console.error("Error advancing task:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container fluid className="vh-100 blue-background d-flex align-items-center justify-content-center">
      <Row className="w-100 justify-content-center">
        <Col md={9} lg={7}>
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "40px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
          >
            {/* Header row */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "16px",
              }}
            >
              <div>
                <h3 style={{ color: "#333", marginBottom: "2px" }}>Dyslexia Screener</h3>
                {studentName && (
                  <p style={{ color: "#555", margin: 0 }}>
                    Student: <strong>{studentName}</strong>
                  </p>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ color: "#888", margin: 0, fontSize: "0.9rem" }}>
                  Task {taskIndex} of {numTasks}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div
              style={{
                background: "#eee",
                borderRadius: "4px",
                height: "8px",
                marginBottom: "28px",
              }}
            >
              <div
                style={{
                  background: "#27AE60",
                  borderRadius: "4px",
                  height: "8px",
                  width: `${progressPercent}%`,
                  transition: "width 0.3s ease",
                }}
              />
            </div>

            <hr style={{ borderColor: "#eee", marginBottom: "28px" }} />

            {/* Task component renders here */}
            <div style={{ marginBottom: "32px" }}>
              {taskComponent || (
                <div
                  style={{
                    border: "2px dashed #ddd",
                    borderRadius: "8px",
                    padding: "48px 40px",
                    textAlign: "center",
                    color: "#aaa",
                  }}
                >
                  <p style={{ fontSize: "1.1rem", marginBottom: "8px", color: "#999" }}>
                    Loading task...
                  </p>
                </div>
              )}
            </div>

            {error && (
              <p style={{ color: "#E74C3C", marginBottom: "16px", textAlign: "center" }}>
                {error}
              </p>
            )}

            {/* Navigation */}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="green-button"
                onClick={handleNext}
                disabled={submitting}
                style={{
                  padding: "12px 40px",
                  fontSize: "1rem",
                  fontWeight: "600",
                  opacity: submitting ? 0.5 : 1,
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
              >
                {submitting ? "Saving..." : isLastTask ? "Finish Screener" : "Next Task →"}
              </button>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default DyslexiaScreenerForm;